import React from "react";
import { FaUserMd, FaUserNurse, FaTimes } from "react-icons/fa";
import { Calendar, Clock, AlertCircle } from "lucide-react";

export default function CareTeamModal({ patient, onClose }) {
  if (!patient) return null;

  const formatTimeRange = (fromTime, toTime) => {
    if (!fromTime && !toTime) return "—";
    const formatSingle = (t) => {
      if (!t) return "";
      const parts = t.trim().split(" ");
      return parts.length > 1 ? parts[1].substring(0, 5) : t;
    };
    return `${formatSingle(fromTime)} - ${formatSingle(toTime)}`;
  };

  const assignments = patient.ipdPatientAssignment || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4  flex items-center justify-between">
          <div>
            <p className="text-xs  mt-1">
              <span className="font-semibold">
                {patient.patientName || "—"}
              </span>{" "}
              ({patient.patientId})
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            title="Close"
          >
            <FaTimes className="text-base" />
          </button>
        </div>

        {/* Patient Context Bar */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap gap-4 text-xs text-slate-600 items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <span className="text-slate-400">Ward & Bed:</span>{" "}
              <span className="font-semibold text-slate-800">
                {patient.wardName || "—"} / Bed {patient.bedNumber || "—"}
              </span>
              {patient.roomNumber && (
                <span className="text-slate-500">
                  {" "}
                  (Room {patient.roomNumber})
                </span>
              )}
            </div>
            <div>
              <span className="text-slate-400">Diagnosis:</span>{" "}
              <span className="font-medium text-slate-800">
                {patient.diagnosis || "—"}
              </span>
            </div>
            <div>
              <span className="text-slate-400">Primary Doctor:</span>{" "}
              <span className="font-semibold text-teal-700">
                {patient.userName || patient.assignedDoctorId || "—"}
              </span>
            </div>
          </div>
          <div>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
              {patient.admissionStatus || "ADMITTED"}
            </span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {assignments.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-base font-semibold text-slate-700">
                No Care Team Assigned
              </p>
              <p className="text-xs text-slate-400 mt-1">
                There are currently no active duty doctor or nurse assignments
                recorded for this patient.
              </p>
            </div>
          ) : (
            assignments.map((assignment, aIdx) => (
              <div
                key={assignment.assignmentId || aIdx}
                className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm"
              >
                <div className="bg-slate-50/80 px-4 py-2.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    {assignment.priority && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                        Priority: {assignment.priority}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {assignment.status || "ACTIVE"}
                    </span>
                  </div>
                </div>

                <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-teal-50 text-teal-600">
                          <FaUserMd className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-slate-800">
                          Duty Doctors
                        </h3>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">
                        {assignment.dutyDoctors?.length || 0}
                      </span>
                    </div>

                    {!assignment.dutyDoctors ||
                    assignment.dutyDoctors.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-2">
                        No duty doctors assigned in this roster.
                      </p>
                    ) : (
                      <div className="space-y-2.5">
                        {assignment.dutyDoctors.map((doc, dIdx) => (
                          <div
                            key={doc.dutyDoctorAssignId || dIdx}
                            className="p-3 rounded-lg border border-teal-100 bg-teal-50/30 hover:bg-teal-50/60 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-slate-800">
                                    {doc.doctorName || "Dr. Unnamed"}
                                  </p>
                                  {doc.primaryDoctor === "Y" && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">
                                  ID:{" "}
                                  <span className="font-mono text-slate-600">
                                    {doc.doctorId || "—"}
                                  </span>
                                </p>
                              </div>
                              <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                {doc.status || "ACTIVE"}
                              </span>
                            </div>

                            <div className="mt-2.5 pt-2 border-t border-teal-100/60 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-slate-400" />
                                <span>{doc.assignDate || "—"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span>
                                  {formatTimeRange(doc.fromTime, doc.toTime)}
                                </span>
                              </div>
                            </div>

                            {doc.remarks && (
                              <p className="text-xs text-slate-500 mt-1.5 italic bg-white/70 p-1.5 rounded">
                                Note: {doc.remarks}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Nurse Assignments Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600">
                          <FaUserNurse className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-slate-800">
                          Assigned Nurses
                        </h3>
                      </div>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                        {assignment.nurseAssignments?.length || 0}
                      </span>
                    </div>

                    {!assignment.nurseAssignments ||
                    assignment.nurseAssignments.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-2">
                        No nurses assigned in this roster.
                      </p>
                    ) : (
                      <div className="space-y-2.5">
                        {assignment.nurseAssignments.map((nurse, nIdx) => (
                          <div
                            key={nurse.nurseAssignmentId || nIdx}
                            className="p-3 rounded-lg border border-indigo-100 bg-indigo-50/30 hover:bg-indigo-50/60 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-slate-800">
                                    {nurse.nurseName || "Nurse Unnamed"}
                                  </p>
                                  {(nurse.primaryNurse === "Y" ||
                                    nurse.primaryNurse === "YES") && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">
                                  ID:{" "}
                                  <span className="font-mono text-slate-600">
                                    {nurse.nurseId || "—"}
                                  </span>
                                </p>
                              </div>
                              <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                {nurse.status || "ACTIVE"}
                              </span>
                            </div>

                            <div className="mt-2.5 pt-2 border-t border-indigo-100/60 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-slate-400" />
                                <span>{nurse.assignDate || "—"}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span>
                                  {formatTimeRange(
                                    nurse.fromTime,
                                    nurse.toTime,
                                  )}
                                </span>
                              </div>
                            </div>

                            {nurse.remarks && (
                              <p className="text-xs text-slate-500 mt-1.5 italic bg-white/70 p-1.5 rounded">
                                Note: {nurse.remarks}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg shadow-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
